import * as React from 'react'
import { CastingDayId } from '../../../../services/models/castings.service'
import { StoreProps } from '../../../../services/stores/casting.store'
import { formatTime, parseTime, partitionArray } from '../../../../services/utils/utils.service'
import { moment } from '../../../../services/utils/moment.service'

type AllowedPattern = StoreProps['data']['slots'][0]['allowed_patterns'][0]

type Params = StoreProps & {
  selectedDays: Date[]
  getFirstDateInstance: (dayId: CastingDayId) => moment.Moment
  getComplexTimes: (dayId: CastingDayId, isStart?: boolean, startedAtHour?: number) => number[]
}

export function useCastingComplexMonoSlot ({ data, store, selectedDays, getComplexTimes, getFirstDateInstance }: Params) {
  const { slots, castingType } = data

  const buildAllowedPattern = React.useCallback((dayId: CastingDayId): AllowedPattern => {
    const fromHour = getComplexTimes(dayId)[0]
    const untilHours = getComplexTimes(dayId, false, fromHour)

    return { day_id: dayId, from_hour: formatTime(fromHour), until_hour: formatTime(untilHours[0]), additional_days: null }
  }, [getComplexTimes])

  const onComplexOneshotDayToggle = React.useCallback((day: Date) => {
    const [newAllowedPatterns, excludedAllowedPatterns] = partitionArray(slots[0].allowed_patterns, _ => _.day_id !== day.getDay())

    if (excludedAllowedPatterns.length === 0) newAllowedPatterns.push(buildAllowedPattern(day.getDay()))
    const minDate = [day].concat(selectedDays).reduce((acc, curr) => acc.getTime() < curr.getTime() ? acc : curr)

    store.setSlots([{ ...slots[0], start_date: moment(minDate).format('YYYY-MM-DD'), allowed_patterns: newAllowedPatterns }])
  }, [buildAllowedPattern, selectedDays, slots, store])

  const onComplexReccurringDayIdToggle = React.useCallback((dayId: CastingDayId) => {
    const [newAllowedPatterns, excludedAllowedPatterns] = partitionArray(slots[0].allowed_patterns, _ => _.day_id !== dayId)
    if (!excludedAllowedPatterns.length) newAllowedPatterns.push(buildAllowedPattern(dayId))
    store.setSlots([{ ...slots[0], allowed_patterns: newAllowedPatterns }])
  }, [buildAllowedPattern, slots, store])

  const addPicker = React.useCallback((dayId: CastingDayId) => {
    store.setSlots([{ ...slots[0], allowed_patterns: [...slots[0].allowed_patterns, buildAllowedPattern(dayId)] }])
  }, [buildAllowedPattern, slots, store])

  const setComplexAllowedPatternField = React.useCallback((oldAllowedPattern: AllowedPattern, changes : { field: 'from_hour' | 'until_hour', value: string }) => {
    const newAllowedPattern = { ...oldAllowedPattern, [changes.field]: changes.value }

    // ensure until hour is still valid after chaning from_hour
    if (changes.field === 'from_hour') {
      const validUntilChoices = getComplexTimes(newAllowedPattern.day_id, false, parseTime(changes.value))
      const preferedUntilHour = parseTime(changes.value) - parseTime(oldAllowedPattern.from_hour) + parseTime(newAllowedPattern.until_hour)
      newAllowedPattern.until_hour = formatTime(validUntilChoices.find(_ => _ === preferedUntilHour) ? preferedUntilHour : validUntilChoices[0])
    }

    const newSlot = { ...slots[0], allowed_patterns: slots[0].allowed_patterns.map(_ => _ === oldAllowedPattern ? newAllowedPattern : _) }
    store.setSlots([newSlot])
  }, [getComplexTimes, slots, store])

  const allowedPatternsByDays = React.useMemo(() => {
    const allowedPatternsByDays: StoreProps['data']['slots'][0]['allowed_patterns'][] = []

    for (const allowedPattern of slots[0].allowed_patterns) {
      let existing = allowedPatternsByDays.find(_ => _[0].day_id === allowedPattern.day_id)
      if (!existing) allowedPatternsByDays.push(existing = [])
      existing.push(allowedPattern)
    }

    return allowedPatternsByDays.sort(([a], [b]) => {
      if (castingType === 'casting_recurring') return (a.day_id ? a.day_id : 7) - (b.day_id ? b.day_id : 7)
      return getFirstDateInstance(a.day_id).valueOf() - getFirstDateInstance(b.day_id).valueOf()
    })
  }, [castingType, getFirstDateInstance, slots])

  const tableDataByDays = React.useMemo(() => {
    return allowedPatternsByDays.map(allowedPatternsByDay => {
      return allowedPatternsByDay.map(allowedPattern => {
        return {
          fromHour: allowedPattern.from_hour,
          untilHour: allowedPattern.until_hour,
          dayId: allowedPattern.day_id,
          setFromHour: (fromHour: string) => setComplexAllowedPatternField(allowedPattern, { field: 'from_hour', value: fromHour }),
          setUntilHour: (untilHour: string) => setComplexAllowedPatternField(allowedPattern, { field: 'until_hour', value: untilHour }),
          remove: () => store.setSlots([{ ...slots[0], allowed_patterns: slots[0].allowed_patterns.filter(_ => _ !== allowedPattern) }]),
          setAllowedPattern: (newAllowedPattern: AllowedPattern) => {
            store.setSlots([{ ...slots[0], allowed_patterns: slots[0].allowed_patterns.map(_ => _ === allowedPattern ? newAllowedPattern : _) }])
          }
        }
      })
    })
  }, [allowedPatternsByDays, setComplexAllowedPatternField, slots, store])

  return { tableDataByDays, onComplexOneshotDayToggle, onComplexReccurringDayIdToggle, addPicker }
}
